import React, {useEffect} from 'react';
import {Grid, Box} from '@mui/material';
import propTypes from 'prop-types';
import {theme} from '../../themes/default-theme';
import styled from '@emotion/styled';
import NextLink from '../../components/navigation/NextLink';
import ReactHtmlParser from 'react-html-parser';

import {RespImg} from '../../components/ui';
import IconLink from '../../components/ui/IconLink';

const PageContainer = styled.div`
  max-width: 1500px;
  font-weight: 700;
	margin: 0 auto;
	
	& .MuiPagination-root {
    ${props => props.paginationTopBorder &&
      `border-top: 1px solid ${theme.palette.middleGrey};`
    }
		margin-left: 16px;
		margin-right: 16px;
		padding-top: 30px;
	}

	& .MuiPagination-ul {
		width: max-content;
		margin: 0 auto;
	}
`

const CommunityGrid = styled(Grid)`
  ${theme.breakpoints.up('md')} {
    margin-bottom: 32px;
  }
	
	& .box-child {
		${theme.breakpoints.up('md')} {
			
			height: 100%;
		}
	}

	a {
		text-decoration: none;
	}

	> a {
		display: block;
		margin-bottom: 19px;

		&:last-child {
			margin-bottom: 0;
		}
	}
`

const StyledGrid = styled.div`
	display: flex;
	gap: 38.5px;
	padding-left: 20px;
	padding-right: 20px;
	
	${theme.breakpoints.down('md')} {
		flex-direction: column;
		gap: 20px;
	}
`

const ThumbnailContainer = styled.div`
	position: relative;
`

const Category = styled.div`
	position: absolute;
	background: ${theme.palette.vividBlue};
	left: 0px;
	top: 0px;
	color: white;
	padding: 3px 10px;
	color: #FFF;
	text-align: center;
	font-size: 12px;
	font-weight: 700;
	line-height: 21px;
	text-transform: capitalize;

	${theme.breakpoints.down('sm')} {
		left: auto;
		right: 0;
	}
`

const CaptionContainer = styled.div`
	padding: 12px;
	margin-top: -4px;
	background: ${theme.palette.white};

	${theme.breakpoints.up('md')} {
		width: 100%;
		padding: 26px;
		padding-bottom: 0;
	}

	* {
		text-align: center;
		min-height: auto !important;
	}
`

const CaptionContainerSmall = styled.div`
	margin-top: -4px;
	background: ${theme.palette.white};
	padding-left: 28px;

	${theme.breakpoints.down('sm')} {
		width: 200%;
		padding-left: 0;
		direction: ltr;
	}

	${theme.breakpoints.up('lg')} {
		padding: 0;
		padding-left: 28px;
		width: 320px;
	}
`

const CommunityTitle = styled.div`
  font-size: 18px;
	font-weight: 700;
	padding-bottom: 4px;
	color: ${theme.palette.charcoal};
	
	${theme.breakpoints.up('md')} {
		min-height: 55px;
		overflow: hidden;
	}

  ${theme.breakpoints.down('sm')} {
		padding-top: 16px;
		padding-bottom: 8px;
  }
`

const CommunityTitleSmall = styled.div`
  font-size: 20px;
	font-weight: 700;
	padding-bottom: 4px;
	color: ${theme.palette.charcoal};

	${theme.breakpoints.up('lg')} {
		display: -webkit-box;
		-webkit-line-clamp: 3;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

  ${theme.breakpoints.down('sm')} {
		font-size: 14px;
		padding-bottom: 8px;
  }
`

const IconLinkContainer = styled.div`
	margin-top: 18px;
	display: block;
`

const CommunityDescription = styled.div`
  font-size: 16px;
	font-weight: 400;
	padding-bottom: 4px;
	color: ${theme.palette.gunmentalGrey};
	margin-top: 10px;
	margin-bottom: 10px;
	
	display: -webkit-box;
	-webkit-line-clamp: 3;
	-webkit-box-orient: vertical;
	overflow: hidden;

	* {
		text-align: center;
	}
	
	${theme.breakpoints.up('md')} {
		display: block;
		min-height: 73px;
	}

  ${theme.breakpoints.down('sm')} {
		font-size: 14px;
  }
`

const CommunityDescriptionSmall = styled.div`
  font-size: 16px;
	font-weight: 400;
	padding-bottom: 4px;
	color: ${theme.palette.gunmentalGrey};
	margin-top: 10px;
	margin-bottom: 10px;

	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;

  ${theme.breakpoints.down('sm')} {
		margin-top: 0;
		font-size: 14px;
  }
`

const CommunityGridItem = styled.div`
  height: 100%;
  overflow: hidden;

	${theme.breakpoints.down('sm')} {
		padding-bottom: 24px;
		border-bottom: 1px solid #D6D6D6;
	}

	${theme.breakpoints.only('md')} {
		padding-bottom: 48px;
		border-bottom: 1px solid #D6D6D6;
	}

	${theme.breakpoints.up('lg')} {
		padding-right: 38.5px;
		border-right: 1px solid #D6D6D6;
	}
`

const CommunityColumnItem = styled.div`
  display: flex;

	${theme.breakpoints.down('sm')} {
		direction: rtl;
		gap: 14px;
	}

	> div:first-child {
		width: 100%;

		${theme.breakpoints.up('md')} {
			max-width: 20%;
		}

		${theme.breakpoints.up('lg')} {
			max-width: 211px;
		}
	}
`

const FeatureContainer = (props) => {
    props = {
        ...props,
        path: typeof props.path === "undefined" ? '/community/article/' : props.path,
        itemsPerPage: typeof props.itemsPerPage === "undefined" ? 6 : props.itemsPerPage,
        paginationTopBorder: typeof props.paginationTopBorder === "undefined" ? true : props.paginationTopBorder,
        searchString: typeof props.searchString === "undefined" ? null : props.searchString,
        borderRadius: typeof props.borderRadius === "undefined" ? false : props.borderRadius,
        communityPage: typeof props.communityPage === "undefined" ? false : props.communityPage
    };

    return (
          <>
              <PageContainer>

                  <StyledGrid>
                      {props.articles[0] &&
                          <CommunityGrid>
                              <CommunityGridItem item>
                                  <NextLink href={process.env.DEALER_SITE ? props.articles[0].uri : props.path + props.articles[0].uri}>
                                      <div>
                                          {props.articles[0].img &&
                                              <ThumbnailContainer>
                                                  <RespImg
                                                      {...props.articles[0].img}
                                                  />
                                                  {props.articles[0].category && <Category dangerouslySetInnerHTML={{__html: props.articles[0].category}} />}
                                              </ThumbnailContainer>
                                          }
                                          <CaptionContainer className={props.type} textCentered={true}>
                                              {props.articles[0].title && <CommunityTitle dangerouslySetInnerHTML={{__html: props.articles[0].title}} />}
                                              {props.articles[0].teaser && <CommunityDescription>{ReactHtmlParser(props.articles[0].teaser)}</CommunityDescription>}
                                              <IconLinkContainer>
                                                  <IconLink colour="vividBlue" href={undefined} link={{ href: undefined, title: 'READ MORE' }} />
                                              </IconLinkContainer>
                                          </CaptionContainer>
                                      </div>
                                  </NextLink>
                              </CommunityGridItem>
                          </CommunityGrid>
                      }
                      {props.articles && 
                          <CommunityGrid item>
                              {props.articles.slice(1, 5).map(function(article, index) {
                                  return (
                                      <NextLink href={process.env.DEALER_SITE ? article.uri : props.path + article.uri}>
                                      
                                              <CommunityColumnItem>
                                                  {article.img &&
                                                      <ThumbnailContainer radius={props.borderRadius ? 1 : 0}>
                                                          <RespImg
                                                              {...article.img}
                                                          />
                                                          {article.category && <Category dangerouslySetInnerHTML={{__html: article.category}} />}
                                                      </ThumbnailContainer>
                                                  }
                                                  <CaptionContainerSmall className={props.type}>
                                                      {article.title && <CommunityTitleSmall>{ReactHtmlParser(article.title)}</CommunityTitleSmall>}
                                                      {article.teaser && <CommunityDescriptionSmall>{ReactHtmlParser(article.teaser)}</CommunityDescriptionSmall>}
                                                  </CaptionContainerSmall>
                                              </CommunityColumnItem>
                                      
                                      </NextLink>
                                  )
                              })}
                      </CommunityGrid>
                      }
                  </StyledGrid>
              </PageContainer>
          </>
    )
}

FeatureContainer.propTypes = {
	path: propTypes.string,
  itemsPerPage: propTypes.number,
  paginationTopBorder: propTypes.bool,
  searchString: propTypes.string,
  borderRadius: propTypes.bool,
	communityPage: propTypes.bool
}

export default FeatureContainer
